import { createContext, useContext, useEffect, useState } from "react";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { useFirebase } from "./FirebaseProvider";
import { AffiliateProviderType } from "../types/providers/AffiliateProvider";
import {
	Affiliate,
	AffiliateDailyHistory,
	AffiliateMonthData,
	AffiliateMonthlyHistory,
} from "../types/affiliate/affiliate";
import { useAuth } from "./AuthenticationProvider";

const AffiliateContext = createContext<AffiliateProviderType>(
	{} as AffiliateProviderType
);

export const AffiliateProvider = ({
	children,
}: {
	children: any;
}): JSX.Element => {
	const { myFS } = useFirebase();
	const { profile } = useAuth();
	const [affiliate, setAffiliate] = useState<Affiliate | null>(null);
	const [creditToBePaid, setCreditToBePaid] = useState<number>(0);

	const [totalEurosMade, setTotalEurosMade] = useState<number>(0);
	const [paymentsMade, setPaymentsMade] = useState<number>(0);
	const [monthlyHistory, setMonthlyHistory] =
		useState<AffiliateMonthlyHistory | null>(null);
	const [dailyHistory, setDailyHistory] =
		useState<AffiliateDailyHistory | null>(null);
	const [totalEurosMadeAffiliateMaster, setTotalEurosMadeAffiliateMaster] =
		useState<number>(0);
	const [paymentsMadeAffiliateMaster, setPaymentsMadeAffiliateMaster] =
		useState<number>(0);

	const [monthlyHistoryAffiliateMaster, setMonthlyHistoryAffiliateMaster] =
		useState<AffiliateMonthlyHistory | null>(null);
	const [dailyHistoryAffiliateMaster, setDailyHistoryAffiliateMaster] =
		useState<AffiliateDailyHistory | null>(null);

	useEffect(() => {
		if (
			profile == null ||
			(profile.role != "Affiliate" && profile.role != "AffiliateMaster")
		)
			return;

		handleGetAffiliateData(profile.uid);
	}, [profile]);

	const handleGetAffiliateData = async (uid: string) => {
		const affiliateData = await getAffiliateData(uid);
		setAffiliate(affiliateData);
	};

	const getAffiliateData = async (uid: string) => {
		let affiliate: Affiliate | null = null;
		try {
			let docRef = doc(myFS, "affiliate", uid);
			const docSnap = await getDoc(docRef);
			if (docSnap.exists()) {
				affiliate = { ...docSnap.data(), uid: uid } as Affiliate;
			}
		} catch (err) {
			console.log(err);
		}
		return affiliate;
	};
	const getMonthlyHistory = async (affiliateUid: string) => {
		if (profile == null) return;
		let traderMonthlyHistory: AffiliateMonthlyHistory = {
			monthlyHistory: [],
		};
		let creditToBePaid = 0;
		let paymentsMade = 0;
		let totalEurosMade = 0;
		let affiliateMasterData = null;
		try {
			if (profile.role == "AffiliateMaster") {
				affiliateMasterData = await getMonthlyAffiliateMasterHistory(
					affiliateUid
				);
			}
			let collectionRef = collection(
				myFS,
				"affiliate",
				affiliateUid,
				"income",
				"code",
				"months"
			);
			const snap = await getDocs(collectionRef);
			if (!snap.empty) {
				for (let i = 0; i < snap.docs.length; i++) {
					const monthData = snap.docs[i].data() as AffiliateMonthData;
					traderMonthlyHistory.monthlyHistory.push({
						...monthData,
						monthId: snap.docs[i].id,
					});
					if (!monthData.paid) {
						creditToBePaid += monthData.amount;
					}
					paymentsMade += monthData.payments;
					totalEurosMade += monthData.amount;
				}
			}
		} catch (err) {
			console.log(err);
		}
		if (affiliateMasterData == null) {
			setTotalEurosMade(totalEurosMade);
			setPaymentsMade(paymentsMade);
			setCreditToBePaid(creditToBePaid);
			setMonthlyHistory(traderMonthlyHistory);
		} else {
			setTotalEurosMade(
				totalEurosMade + affiliateMasterData.totalEurosMade
			);
			setPaymentsMade(paymentsMade + affiliateMasterData.paymentsMade);
			setCreditToBePaid(
				creditToBePaid + affiliateMasterData.creditToBePaid
			);
			setMonthlyHistory(traderMonthlyHistory);
		}
	};
	const getDailyHistory = async (affiliateUid: string) => {
		let traderMonthlyHistory: AffiliateMonthlyHistory = {
			monthlyHistory: [],
		};
		let creditToBePaid = 0;
		let paymentsMade = 0;
		let totalEurosMade = 0;
		try {
			let collectionRef = collection(
				myFS,
				"affiliate",
				affiliateUid,
				"income",
				"code",
				"days"
			);
			const snap = await getDocs(collectionRef);
			console.log(snap.empty);
			if (snap.empty) return;
			for (let i = 0; i < snap.docs.length; i++) {
				const monthData = snap.docs[i].data() as AffiliateMonthData;
				traderMonthlyHistory.monthlyHistory.push({
					...monthData,
					monthId: snap.docs[i].id,
				});
				if (!monthData.paid) {
					creditToBePaid += monthData.amount;
				}
				paymentsMade += monthData.payments;
				totalEurosMade += monthData.amount;
			}
		} catch (err) {
			console.log(err);
		}
		// setTotalEurosMade(totalEurosMade);
		// setPaymentsMade(paymentsMade);
		// setCreditToBePaid(creditToBePaid);
		// setMonthlyHistory(traderMonthlyHistory);
	};
	const getMonthlyAffiliateMasterHistory = async (
		affiliateUid: string
	): Promise<null | {
		creditToBePaid: number;
		totalEurosMade: number;
		paymentsMade: number;
	}> => {
		console.log("getMonthlyAffiliateMasterHistory");
		let traderMonthlyHistory: AffiliateMonthlyHistory = {
			monthlyHistory: [],
		};
		let creditToBePaid = 0;
		let paymentsMade = 0;
		let totalEurosMade = 0;
		try {
			let collectionRef = collection(
				myFS,
				"affiliate",
				affiliateUid,
				"income",
				"affiliate",
				"months"
			);
			const snap = await getDocs(collectionRef);
			if (snap.empty) return null;
			for (let i = 0; i < snap.docs.length; i++) {
				const monthData = snap.docs[i].data() as AffiliateMonthData;
				traderMonthlyHistory.monthlyHistory.push({
					...monthData,
					monthId: snap.docs[i].id,
				});
				if (!monthData.paid) {
					creditToBePaid += monthData.amount;
				}
				paymentsMade += monthData.payments;
				totalEurosMade += monthData.amount;
			}
		} catch (err) {
			console.log(err);
		}
		setTotalEurosMadeAffiliateMaster(totalEurosMade);
		setPaymentsMadeAffiliateMaster(paymentsMade);
		setMonthlyHistoryAffiliateMaster(traderMonthlyHistory);
		return {
			creditToBePaid: creditToBePaid,
			totalEurosMade: totalEurosMade,
			paymentsMade: paymentsMade,
		};
	};
	const getDailyAffiliateMasterHistory = async (affiliateUid: string) => {
		let traderMonthlyHistory: AffiliateMonthlyHistory = {
			monthlyHistory: [],
		};
		let creditToBePaid = 0;
		let paymentsMade = 0;
		let totalEurosMade = 0;
		try {
			let collectionRef = collection(
				myFS,
				"affiliate",
				affiliateUid,
				"income",
				"affiliate",
				"days"
			);
			const snap = await getDocs(collectionRef);
			console.log(snap.empty);
			if (snap.empty) return;
			for (let i = 0; i < snap.docs.length; i++) {
				const monthData = snap.docs[i].data() as AffiliateMonthData;
				traderMonthlyHistory.monthlyHistory.push({
					...monthData,
					monthId: snap.docs[i].id,
				});
				if (!monthData.paid) {
					creditToBePaid += monthData.amount;
				}
				paymentsMade += monthData.payments;
				totalEurosMade += monthData.amount;
			}
		} catch (err) {
			console.log(err);
		}
		// setTotalEurosMadeAffiliateMaster(totalEurosMade);
		// setPaymentsMadeAffiliateMaster(paymentsMade);
		// setMonthlyHistoryAffiliateMaster(traderMonthlyHistory);
		// setCreditToBePaid(creditToBePaid);
	};

	const affiliateValues: AffiliateProviderType = {
		affiliate,
		getAffiliateData,
		creditToBePaid,
		paymentsMade,
		monthlyHistory,
		monthlyHistoryAffiliateMaster,
		getMonthlyHistory,
		totalEurosMade,
	};

	return (
		<AffiliateContext.Provider value={affiliateValues}>
			{children}
		</AffiliateContext.Provider>
	);
};

export const useAffiliateProvider = () => useContext(AffiliateContext);
